/**
 * Multi-select cascade rail used by the inventory ledger, replenishment
 * and forecast pages (and any other view that needs to scope by
 * vendor / brand / taxonomy + product name).
 *
 * Cascade rules — each step constrains everything to its right; an empty
 * upstream selection means "all":
 *
 *     Vendor → Category Group → Category → Sub-Category
 *                              ↘ Brand (vendor-scoped)
 *
 *   - Vendor selection clamps Brand options to the vendor's
 *     `brand_ids` and Sub-Category options to its `subcategory_ids`. The
 *     allowed Sub-Categories then map up to allowed Categories (through
 *     `subcategory.category_ids`) and Category Groups (through
 *     `category.category_group_id`). Picking only a vendor never empties
 *     the downstream pickers.
 *   - Picking Category Group narrows Category, Sub-Category and Brand to
 *     things that live under those groups (intersected with the vendor
 *     constraint, if any).
 *   - Picking Category narrows Sub-Category (intersected with anything
 *     above).
 *
 * The rail also auto-prunes stale selections whenever an upstream filter
 * change makes them invalid, so the parent's filter state is always
 * coherent without the page having to babysit cleanup callbacks.
 *
 * Pure-controlled — every value lives in the parent so React Query keys
 * stay stable and URL hydration "just works".
 */
import { useEffect, useMemo, useRef } from 'react';
import { Card, CardBody } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { MultiCombobox } from '@/components/ui/MultiCombobox';
import type { InventoryTaxonomyResponse } from '@/api/bi';

export interface TaxonomyRailProps {
  taxonomy: InventoryTaxonomyResponse | undefined;
  taxonomyLoading: boolean;
  vendorIds: number[];
  onVendorIds: (ids: number[]) => void;
  brandIds: number[];
  onBrandIds: (ids: number[]) => void;
  categoryGroupIds: number[];
  onCategoryGroupIds: (ids: number[]) => void;
  categoryIds: number[];
  onCategoryIds: (ids: number[]) => void;
  subcategoryIds: number[];
  onSubcategoryIds: (ids: number[]) => void;
  q: string;
  onQ: (v: string) => void;
  onClear?: () => void;
  /** Optional slot to append local controls (e.g. status select) without
   *  needing a second card. */
  trailing?: React.ReactNode;
  /** Hide the vendor picker for pages where vendor doesn't apply yet. */
  hideVendor?: boolean;
  /**
   * `card` (default) renders the classic horizontal rail in its own Card;
   * `plain` renders a vertical stack with no wrapper, for embedding inside
   * side panels / drawers (e.g. the 3D map filter panel).
   */
  variant?: 'card' | 'plain';
}

function intersect(a: Set<number> | null, b: Set<number> | null): Set<number> | null {
  if (!a) return b;
  if (!b) return a;
  const out = new Set<number>();
  for (const v of a) if (b.has(v)) out.add(v);
  return out;
}

function setsEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const sa = new Set(a);
  for (const v of b) if (!sa.has(v)) return false;
  return true;
}

export function TaxonomyRail({
  taxonomy,
  taxonomyLoading,
  vendorIds, onVendorIds,
  brandIds, onBrandIds,
  categoryGroupIds, onCategoryGroupIds,
  categoryIds, onCategoryIds,
  subcategoryIds, onSubcategoryIds,
  q, onQ,
  onClear,
  trailing,
  hideVendor,
  variant = 'card',
}: TaxonomyRailProps) {
  const vendors = taxonomy?.vendors ?? [];
  const brands = taxonomy?.brands ?? [];
  const groups = taxonomy?.category_groups ?? [];
  const allCategories = taxonomy?.categories ?? [];
  const allSubcategories = taxonomy?.subcategories ?? [];

  // ─── Cascade derivations ─────────────────────────────────────────────
  // Step 1: derive what the *vendor* selection permits. When no vendor is
  // picked, `null` means "no constraint" (all rows).

  const vendorAllowedSubIds = useMemo<Set<number> | null>(() => {
    if (vendorIds.length === 0) return null;
    const out = new Set<number>();
    for (const v of vendors) {
      if (vendorIds.includes(v.id)) for (const sid of v.subcategory_ids) out.add(sid);
    }
    return out;
  }, [vendors, vendorIds]);

  const vendorAllowedBrandIds = useMemo<Set<number> | null>(() => {
    if (vendorIds.length === 0) return null;
    const out = new Set<number>();
    for (const v of vendors) {
      if (vendorIds.includes(v.id)) for (const bid of v.brand_ids) out.add(bid);
    }
    return out;
  }, [vendors, vendorIds]);

  // Categories implied by the vendor-allowed sub-cats. We project up
  // through `subcategory.category_ids` so that picking a vendor that
  // only supplies a single sub doesn't blow away the Category picker.
  const vendorAllowedCategoryIds = useMemo<Set<number> | null>(() => {
    if (!vendorAllowedSubIds) return null;
    const out = new Set<number>();
    for (const s of allSubcategories) {
      if (!vendorAllowedSubIds.has(s.id)) continue;
      for (const cid of s.category_ids) out.add(cid);
    }
    return out;
  }, [allSubcategories, vendorAllowedSubIds]);

  const vendorAllowedGroupIds = useMemo<Set<number> | null>(() => {
    if (!vendorAllowedCategoryIds) return null;
    const out = new Set<number>();
    for (const c of allCategories) {
      if (!vendorAllowedCategoryIds.has(c.id)) continue;
      if (c.category_group_id != null) out.add(c.category_group_id);
    }
    return out;
  }, [allCategories, vendorAllowedCategoryIds]);

  // ─── Visible option lists (vendor + upstream group/category filters) ─

  const visibleGroups = useMemo(() => {
    if (!vendorAllowedGroupIds) return groups;
    return groups.filter((g) => vendorAllowedGroupIds.has(g.id));
  }, [groups, vendorAllowedGroupIds]);

  const visibleCategories = useMemo(() => {
    const groupSet =
      categoryGroupIds.length > 0 ? new Set(categoryGroupIds) : null;
    return allCategories.filter((c) => {
      if (groupSet && (c.category_group_id == null || !groupSet.has(c.category_group_id))) {
        return false;
      }
      if (vendorAllowedCategoryIds && !vendorAllowedCategoryIds.has(c.id)) {
        return false;
      }
      return true;
    });
  }, [allCategories, categoryGroupIds, vendorAllowedCategoryIds]);

  const visibleSubcategories = useMemo(() => {
    const catSet = categoryIds.length > 0 ? new Set(categoryIds) : null;
    // When category isn't picked but a group is, fall back to the
    // allowed-categories list so the sub picker still narrows down.
    const fallbackCatIds =
      catSet == null && categoryGroupIds.length > 0
        ? new Set(visibleCategories.map((c) => c.id))
        : null;
    const effectiveCatSet = catSet ?? fallbackCatIds;
    return allSubcategories.filter((s) => {
      if (effectiveCatSet && !s.category_ids.some((id) => effectiveCatSet.has(id))) {
        return false;
      }
      if (vendorAllowedSubIds && !vendorAllowedSubIds.has(s.id)) return false;
      return true;
    });
  }, [
    allSubcategories,
    categoryIds,
    categoryGroupIds,
    visibleCategories,
    vendorAllowedSubIds,
  ]);

  const visibleBrands = useMemo(() => {
    if (!vendorAllowedBrandIds) return brands;
    return brands.filter((b) => vendorAllowedBrandIds.has(b.id));
  }, [brands, vendorAllowedBrandIds]);

  // ─── Auto-prune stale selections when upstream changes ───────────────
  // We only want to prune *after* the taxonomy has hydrated — pruning
  // against an empty taxonomy on first render would wipe everything the
  // user pre-loaded from the URL.
  const ready = !taxonomyLoading && taxonomy != null;
  // `useRef` avoids running the prune effects in a loop when the parent
  // re-renders with the same value reference.
  const lastSyncedKey = useRef<string>('');

  useEffect(() => {
    if (!ready) return;
    const validIds = new Set(visibleBrands.map((b) => b.id));
    const next = brandIds.filter((id) => validIds.has(id));
    if (!setsEqual(next, brandIds)) onBrandIds(next);
  // We intentionally skip onBrandIds in deps — parent passes a fresh fn
  // each render and would cause an infinite loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, visibleBrands, brandIds]);

  useEffect(() => {
    if (!ready) return;
    const validIds = new Set(visibleGroups.map((g) => g.id));
    const next = categoryGroupIds.filter((id) => validIds.has(id));
    if (!setsEqual(next, categoryGroupIds)) onCategoryGroupIds(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, visibleGroups, categoryGroupIds]);

  useEffect(() => {
    if (!ready) return;
    const validIds = new Set(visibleCategories.map((c) => c.id));
    const next = categoryIds.filter((id) => validIds.has(id));
    if (!setsEqual(next, categoryIds)) onCategoryIds(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, visibleCategories, categoryIds]);

  useEffect(() => {
    if (!ready) return;
    const validIds = new Set(visibleSubcategories.map((s) => s.id));
    const next = subcategoryIds.filter((id) => validIds.has(id));
    if (!setsEqual(next, subcategoryIds)) onSubcategoryIds(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, visibleSubcategories, subcategoryIds]);

  // Mark the cascade as synced for any consumers that care (debug).
  useEffect(() => {
    if (!ready) return;
    lastSyncedKey.current = [
      vendorIds.join(','),
      brandIds.join(','),
      categoryGroupIds.join(','),
      categoryIds.join(','),
      subcategoryIds.join(','),
    ].join('|');
  }, [
    ready, vendorIds, brandIds, categoryGroupIds, categoryIds, subcategoryIds,
  ]);

  const hasAnySelection =
    vendorIds.length > 0 ||
    brandIds.length > 0 ||
    categoryGroupIds.length > 0 ||
    categoryIds.length > 0 ||
    subcategoryIds.length > 0 ||
    q.trim().length > 0;

  const fields = (
    <>
        {!hideVendor ? (
          <RailField label="Vendor">
            <MultiCombobox
              values={vendorIds.map(String)}
              onChange={(v) => onVendorIds(v.map(Number))}
              options={vendors.map((v) => ({ value: String(v.id), label: v.name }))}
              loading={taxonomyLoading}
              width={220}
              allLabel="All vendors"
              countNoun="vendor"
              placeholder="All vendors"
              searchPlaceholder="Search vendor…"
              emptyMessage="No vendor matches"
            />
          </RailField>
        ) : null}

        <RailField label="Category group">
          <MultiCombobox
            values={categoryGroupIds.map(String)}
            onChange={(v) => onCategoryGroupIds(v.map(Number))}
            options={visibleGroups.map((g) => ({ value: String(g.id), label: g.name }))}
            loading={taxonomyLoading}
            width={200}
            allLabel="All groups"
            countNoun="group"
            placeholder="All groups"
            searchPlaceholder="Search group…"
            emptyMessage="No groups match the vendor filter"
          />
        </RailField>

        <RailField label="Category">
          <MultiCombobox
            values={categoryIds.map(String)}
            onChange={(v) => onCategoryIds(v.map(Number))}
            options={visibleCategories.map((c) => ({ value: String(c.id), label: c.name }))}
            loading={taxonomyLoading}
            width={200}
            allLabel="All categories"
            countNoun="category"
            placeholder="All categories"
            searchPlaceholder="Search category…"
            emptyMessage="No categories match the upstream filter"
          />
        </RailField>

        <RailField label="Sub-category">
          <MultiCombobox
            values={subcategoryIds.map(String)}
            onChange={(v) => onSubcategoryIds(v.map(Number))}
            options={visibleSubcategories.map((s) => ({ value: String(s.id), label: s.name }))}
            loading={taxonomyLoading}
            width={220}
            allLabel="All sub-categories"
            countNoun="sub-cat"
            placeholder="All sub-categories"
            searchPlaceholder="Search sub-category…"
            emptyMessage="No sub-categories match the upstream filter"
          />
        </RailField>

        <RailField label="Brand">
          <MultiCombobox
            values={brandIds.map(String)}
            onChange={(v) => onBrandIds(v.map(Number))}
            options={visibleBrands.map((b) => ({ value: String(b.id), label: b.name }))}
            loading={taxonomyLoading}
            width={200}
            allLabel="All brands"
            countNoun="brand"
            placeholder="All brands"
            searchPlaceholder="Search brand…"
            emptyMessage="No brands match the vendor filter"
          />
        </RailField>

        <div className="flex min-w-[220px] flex-1 flex-col gap-1">
          <label className="text-[11px] font-medium text-muted-foreground">Search product</label>
          <Input
            placeholder="Product name contains…"
            value={q}
            onChange={(e) => onQ(e.target.value)}
          />
        </div>

        {trailing}
        {onClear && hasAnySelection ? (
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium text-transparent select-none">·</label>
            <Button variant="ghost" size="sm" onClick={onClear} className="h-9">
              Clear filters
            </Button>
          </div>
        ) : null}

        {/* Intersection diagnostic kept silent; helps when debugging
            why a downstream picker is empty. We expose nothing in DOM. */}
        <span className="sr-only" aria-hidden>
          {intersect(vendorAllowedSubIds, null) ? '' : ''}
        </span>
    </>
  );

  if (variant === 'plain') {
    return <div className="flex flex-col gap-3">{fields}</div>;
  }

  return (
    <Card className="mb-4">
      <CardBody className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        {fields}
      </CardBody>
    </Card>
  );
}

interface RailFieldProps {
  label: string;
  children: React.ReactNode;
}

function RailField({ label, children }: RailFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
